import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import type { PaginationState } from '@tanstack/react-table'
import { getPaginationRowModel, getCoreRowModel } from '@tanstack/react-table'
import { RefreshCw } from 'lucide-react'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai'

import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { Button } from '@/components/ui/button'
import { FeedbackSummaryBar } from '@/components/ui/feedback-bar'
import { PageHeader } from '@/components/ui/page-header'

import {
    fetchRobotFeedbackSummary,
    fetchRobotFeedbacks,
    fetchSatisfactionScores,
} from '@/api/feedbacks.api'
import type { RobotFeedback } from '@/api/feedbacks.api'
import { buildRobotColumns } from './columnsRobot'
import { RobotFeedbackFilterPanel } from './RobotFeedbackFilterPanel'
import type { RobotFeedbackFilterValues } from './RobotFeedbackFilterPanel'
import { currentUserAtom } from '@/store/auth.atom'
import { formatEndDate, formatStartDate } from '@/utils/format'
import { exportRobotSurvey, useTableExport } from '@/api/dowload-excel.api'
import { usePermission } from '@/hooks/use-permission'

const DEFAULT_FILTER: RobotFeedbackFilterValues = {
    robotId: undefined,
    robotName: undefined,
    dateRange: undefined,
    unitId: undefined,
    unitName: undefined,
}

function toApiParams(f: RobotFeedbackFilterValues) {
    const fromDateRaw = f.dateRange?.from
    const toDateRaw = f.dateRange?.to

    return {
        robotId: f.robotId || undefined,
        robotName: f.robotName || undefined,
        unitId: f.unitId || undefined,
        fromDate: formatStartDate(fromDateRaw || dayjs().startOf('month')),
        toDate: formatEndDate(toDateRaw || dayjs()),
    }
}

export function RobotFeedbackTab() {
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    })

    const currentUser = useAtomValue(currentUserAtom)
    const showUnitColumn = currentUser?.userType === 1
    const canExport = usePermission('survey-export')

    const [showFilter, setShowFilter] = useState(false)
    const [draftFilter, setDraftFilter] = useState<RobotFeedbackFilterValues>(DEFAULT_FILTER)
    const [appliedFilter, setAppliedFilter] = useState<RobotFeedbackFilterValues>(DEFAULT_FILTER)
    const resolvedUnitId = showUnitColumn ? appliedFilter.unitId : currentUser?.unitId
    const [selectedScores, setSelectedScores] = useState<number[]>([])
    const [isExporting, setIsExporting] = useState(false)

    const apiParams = useMemo(() => {
        const base = toApiParams({
            ...appliedFilter,
            unitId: resolvedUnitId,
        })

        return {
            ...base,
            satisfactionScore: selectedScores.length ? selectedScores : undefined,
        }
    }, [appliedFilter, resolvedUnitId, selectedScores])

    const { data: satisfactionScores = [] } = useQuery({
        queryKey: ['satisfaction-scores'],
        queryFn: ({ signal }) => fetchSatisfactionScores(signal),
        refetchOnMount: 'always',
        staleTime: Infinity,
    })

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['robot-feedbacks', pageIndex, pageSize, apiParams],
        queryFn: ({ signal }) =>
            fetchRobotFeedbacks(pageIndex, pageSize, apiParams, signal),
        refetchOnMount: 'always',
        refetchInterval: 30 * 60 * 1000,
    })

    const hasData = Boolean(data?.items && data.items.length > 0)

    const summaryFilters = useMemo(() => {
        return toApiParams({
            ...appliedFilter,
            unitId: resolvedUnitId,
        })
    }, [appliedFilter, resolvedUnitId])

    const { data: summary, refetch: refetchSummary } = useQuery({
        queryKey: ['robot-feedbacks', 'summary', summaryFilters],
        queryFn: ({ signal }) =>
            fetchRobotFeedbackSummary(
                satisfactionScores,
                summaryFilters,
                signal,
            ),
        enabled: satisfactionScores.length > 0,
        refetchOnMount: 'always',
        refetchInterval: 30 * 60 * 1000,
    })

    const columns = useMemo(
        () =>
            buildRobotColumns({
                pageIndex,
                pageSize,
                showUnitColumn,
            }),
        [pageIndex, pageSize, showUnitColumn],
    )

    const { handleExport } = useTableExport<RobotFeedback>(
        'Danh sách đánh giá robot',
        columns,
        async (payload) => {
            try {
                setIsExporting(true)
                await exportRobotSurvey(
                    { page: pageIndex, size: pageSize, ...apiParams },
                    payload,
                    'danh-sach-danh-gia-robot.xlsx',
                )
            } finally {
                setIsExporting(false)
            }
        },
    )

    return (
        <div className="space-y-4">
            <div className="px-6 !mb--7">
                <PageHeader
                    title="DANH SÁCH ĐÁNH GIÁ ROBOT"
                    onToggleFilter={() => setShowFilter((v) => !v)}
                    onExport={canExport ? handleExport : undefined}
                    disabled={!hasData}
                    isExporting={isExporting}
                >
                    <Button
                        variant="primary"
                        size="md"
                        disabled={isLoading}
                        onClick={() => {
                            setDraftFilter(DEFAULT_FILTER)
                            setAppliedFilter(DEFAULT_FILTER)
                            setSelectedScores([])
                            setPagination((s) => ({ ...s, pageIndex: 0 }))
                            refetch()
                            refetchSummary()
                        }}
                        className="flex-1 gap-2 sm:flex-none h-10"
                    >
                        <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                        Làm mới
                    </Button>
                </PageHeader>
            </div>

            {showFilter && (
                <RobotFeedbackFilterPanel
                    values={draftFilter}
                    onChange={setDraftFilter}
                    onSearch={() => {
                        setAppliedFilter(draftFilter)
                        setPagination((s) => ({ ...s, pageIndex: 0 }))
                        refetch()
                    }}
                />
            )}

            <DataTable
                columns={columns}
                data={data?.items ?? []}
                isLoading={isLoading}
                headertable={
                    summary ? (
                        <FeedbackSummaryBar
                            data={summary}
                            selectedScores={selectedScores}
                            onToggleScore={(score) => {
                                setSelectedScores((prev) => {
                                    const exists = prev.includes(score)
                                    const next = exists ? prev.filter((s) => s !== score) : [...prev, score]
                                    return next
                                })
                                setPagination((s) => ({ ...s, pageIndex: 0 }))
                            }}
                        />
                    ) : null
                }
                tableOptions={{
                    state: {
                        pagination: { pageIndex, pageSize },
                    },
                    onPaginationChange: setPagination,
                    getPaginationRowModel: getPaginationRowModel(),
                    getCoreRowModel: getCoreRowModel(),
                    manualPagination: true,
                    pageCount: data?.totalPages,
                }}
                footer={
                    <Pagination
                        page={pageIndex + 1}
                        pageCount={data?.totalPages ?? 1}
                        pageSize={pageSize}
                        totalCount={data?.total ?? 0}
                        onPageChange={(p) => setPagination((s) => ({ ...s, pageIndex: p - 1 }))}
                        onPageSizeChange={(size) => setPagination({ pageIndex: 0, pageSize: size })}
                    />
                }
            />
        </div>
    )
}
